/**
 * @file useSwitchEx.js
 * @description フォーム用スイッチフィールドを簡単に生成するためのカスタムフック
 *
 * このフックは、Material-UIのSwitchコンポーネントを使用して、
 * スタイリッシュで機能的なフォームスイッチフィールドを提供します。
 * データベーススキーマのboolean、tinyint型カラムから自動生成されます。
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { switch_status, switch_is_deleted } = useSwitchEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {switch_status({ label: "ステータス" })}
 *     {switch_is_deleted({ label: "削除済み" })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-12-19
 */

"use client";

import logjs from "@metrojs/logjs";
const log = new logjs("useSwitchEx");

/**
 * スイッチフィールドを生成するためのカスタムフック
 * @param {Object} form - フォームデータオブジェクト
 * @param {Object} formProps - フォームプロパティオブジェクト
 * @returns {Object} 各種スイッチフィールド生成関数のオブジェクト
 */
const useSwitchEx = (form = {}, formProps = {}) => {
  /**
   * 締め処理フラグ　1=部品、3=車輌スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締め処理フラグ　1=部品、3=車輌スイッチのJSX要素
   */
  const switch_Wellset_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Wellset_flg form.Wellset_flg:${form?.Wellset_flg || ""} params:`, params);
    return formProps.toggleSwitch("Wellset_flg", { label: "締め処理フラグ　1=部品、3=車輌", ...params }, is_debug);
  };
  /**
   * ダウンロードフラグ 1=済スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ダウンロードフラグ 1=済スイッチのJSX要素
   */
  const switch_dl_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_dl_flg form.dl_flg:${form?.dl_flg || ""} params:`, params);
    return formProps.toggleSwitch("dl_flg", { label: "ダウンロードフラグ 1=済", ...params }, is_debug);
  };
  /**
   * 書類有無　1:有　9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類有無　1:有　9:無スイッチのJSX要素
   */
  const switch_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Shorui_flg form.Shorui_flg:${form?.Shorui_flg || ""} params:`, params);
    return formProps.toggleSwitch("Shorui_flg", { label: "書類有無　1:有　9:無", ...params }, is_debug);
  };
  /**
   * 書類送料 1:あり 0:なしスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送料 1:あり 0:なしスイッチのJSX要素
   */
  const switch_ShoruiSoryo_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_ShoruiSoryo_Flg form.ShoruiSoryo_Flg:${form?.ShoruiSoryo_Flg || ""} params:`, params);
    return formProps.toggleSwitch("ShoruiSoryo_Flg", { label: "書類送料 1:あり 0:なし", ...params }, is_debug);
  };
  /**
   * 書類送付書フラグ 1:あり 9:なしスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送付書フラグ 1:あり 9:なしスイッチのJSX要素
   */
  const switch_ShoruiSoufu_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_ShoruiSoufu_Flg form.ShoruiSoufu_Flg:${form?.ShoruiSoufu_Flg || ""} params:`, params);
    return formProps.toggleSwitch("ShoruiSoufu_Flg", { label: "書類送付書フラグ 1:あり 9:なし", ...params }, is_debug);
  };
  /**
   * 画像ありチェック 1=ありスイッチ（お知らせ検索用）
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 画像ありチェックのJSX要素
   */
  const switch_ImgCheck = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_ImgCheck form.ImgCheck:${form?.ImgCheck || ""} params:`, params);
    return formProps.toggleSwitch("ImgCheck", { label: "画像あり", ...params }, is_debug);
  };
  /**
   * 非公開は含まないチェック 1=含まないスイッチ（お知らせ検索用）
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 非公開は含まないチェックのJSX要素
   */
  const switch_OpenCheck = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_OpenCheck form.OpenCheck:${form?.OpenCheck || ""} params:`, params);
    return formProps.toggleSwitch("OpenCheck", { label: "非公開は含まない", ...params }, is_debug);
  };
  /**
   * お問い合わせ種類　1:生産者 2:整備スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} お問い合わせ種類　1:生産者 2:整備スイッチのJSX要素
   */
  const switch_ContactKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_ContactKind form.ContactKind:${form?.ContactKind || ""} params:`, params);
    return formProps.toggleSwitch("ContactKind", { label: "お問い合わせ種類　1:生産者 2:整備", ...params }, is_debug);
  };
  /**
   * 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品スイッチのJSX要素
   */
  const switch_Buysell_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Buysell_flg form.Buysell_flg:${form?.Buysell_flg || ""} params:`, params);
    return formProps.toggleSwitch("Buysell_flg", { label: "販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品", ...params }, is_debug);
  };
  /**
   * 手数料除外フラグ 1=除外するスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手数料除外フラグ 1=除外するスイッチのJSX要素
   */
  const switch_Tesuryo_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Tesuryo_flg form.Tesuryo_flg:${form?.Tesuryo_flg || ""} params:`, params);
    return formProps.toggleSwitch("Tesuryo_flg", { label: "手数料除外フラグ 1=除外する", ...params }, is_debug);
  };
  /**
   * 車輌販売表示フラグ 1=非表示スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌販売表示フラグ 1=非表示スイッチのJSX要素
   */
  const switch_CarView_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CarView_flg form.CarView_flg:${form?.CarView_flg || ""} params:`, params);
    return formProps.toggleSwitch("CarView_flg", { label: "車輌販売表示フラグ 1=非表示", ...params }, is_debug);
  };
  /**
   * 車輌：書類有無 1:有 5:どちらでも 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：書類有無 1:有 5:どちらでも 9:無スイッチのJSX要素
   */
  const switch_C_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Shorui_flg form.C_Shorui_flg:${form?.C_Shorui_flg || ""} params:`, params);
    return formProps.toggleSwitch("C_Shorui_flg", { label: "車輌：書類有無 1:有 5:どちらでも 9:無", ...params }, is_debug);
  };
  /**
   * 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品スイッチのJSX要素
   */
  const switch_Hacchu_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Hacchu_flg form.Hacchu_flg:${form?.Hacchu_flg || ""} params:`, params);
    return formProps.toggleSwitch("Hacchu_flg", { label: "発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品", ...params }, is_debug);
  };
  /**
   * 車輌：リサイクル有無 1:有 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：リサイクル有無 1:有 9:無スイッチのJSX要素
   */
  const switch_C_Recycle_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Recycle_flg form.C_Recycle_flg:${form?.C_Recycle_flg || ""} params:`, params);
    return formProps.toggleSwitch("C_Recycle_flg", { label: "車輌：リサイクル有無 1:有 9:無", ...params }, is_debug);
  };
  /**
   * 車輌：車歴 1:自家用 2:レンタカースイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車歴 1:自家用 2:レンタカースイッチのJSX要素
   */
  const switch_C_Shareki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Shareki form.C_Shareki:${form?.C_Shareki || ""} params:`, params);
    return formProps.toggleSwitch("C_Shareki", { label: "車輌：車歴 1:自家用 2:レンタカー", ...params }, is_debug);
  };
  /**
   * 車輌：修復歴スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：修復歴スイッチのJSX要素
   */
  const switch_C_Shufuku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Shufuku form.C_Shufuku:${form?.C_Shufuku || ""} params:`, params);
    return formProps.toggleSwitch("C_Shufuku", { label: "車輌：修復歴", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　前部スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　前部スイッチのJSX要素
   */
  const switch_C_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou01 form.C_Sonshou01:${form?.C_Sonshou01 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou01", { label: "車輌：損傷　前部", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　後部スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　後部スイッチのJSX要素
   */
  const switch_C_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou02 form.C_Sonshou02:${form?.C_Sonshou02 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou02", { label: "車輌：損傷　後部", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　右側スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　右側スイッチのJSX要素
   */
  const switch_C_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou03 form.C_Sonshou03:${form?.C_Sonshou03 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou03", { label: "車輌：損傷　右側", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　左側スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　左側スイッチのJSX要素
   */
  const switch_C_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou04 form.C_Sonshou04:${form?.C_Sonshou04 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou04", { label: "車輌：損傷　左側", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　ルーフスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　ルーフスイッチのJSX要素
   */
  const switch_C_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou05 form.C_Sonshou05:${form?.C_Sonshou05 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou05", { label: "車輌：損傷　ルーフ", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　盗難スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　盗難スイッチのJSX要素
   */
  const switch_C_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou06 form.C_Sonshou06:${form?.C_Sonshou06 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou06", { label: "車輌：損傷　盗難", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　水没スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　水没スイッチのJSX要素
   */
  const switch_C_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou07 form.C_Sonshou07:${form?.C_Sonshou07 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou07", { label: "車輌：損傷　水没", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　炎上スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　炎上スイッチのJSX要素
   */
  const switch_C_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou08 form.C_Sonshou08:${form?.C_Sonshou08 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou08", { label: "車輌：損傷　炎上", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　エアバッグ展開スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　エアバッグ展開スイッチのJSX要素
   */
  const switch_C_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou09 form.C_Sonshou09:${form?.C_Sonshou09 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou09", { label: "車輌：損傷　エアバッグ展開", ...params }, is_debug);
  };
  /**
   * 車輌：損傷　横転スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　横転スイッチのJSX要素
   */
  const switch_C_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Sonshou10 form.C_Sonshou10:${form?.C_Sonshou10 || ""} params:`, params);
    return formProps.toggleSwitch("C_Sonshou10", { label: "車輌：損傷　横転", ...params }, is_debug);
  };
  /**
   * 車輌：足回り 1:不明 5:OK 9:NGスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回り 1:不明 5:OK 9:NGスイッチのJSX要素
   */
  const switch_C_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_Ashimawari form.C_Ashimawari:${form?.C_Ashimawari || ""} params:`, params);
    return formProps.toggleSwitch("C_Ashimawari", { label: "車輌：足回り 1:不明 5:OK 9:NG", ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左前 1:有 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左前 1:有 9:無スイッチのJSX要素
   */
  const switch_C_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_AshimawariNG1 form.C_AshimawariNG1:${form?.C_AshimawariNG1 || ""} params:`, params);
    return formProps.toggleSwitch("C_AshimawariNG1", { label: "車輌：足回りNGの場合 左前 1:有 9:無", ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右前 1:有 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右前 1:有 9:無スイッチのJSX要素
   */
  const switch_C_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_AshimawariNG2 form.C_AshimawariNG2:${form?.C_AshimawariNG2 || ""} params:`, params);
    return formProps.toggleSwitch("C_AshimawariNG2", { label: "車輌：足回りNGの場合 右前 1:有 9:無", ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左後 1:有 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左後 1:有 9:無スイッチのJSX要素
   */
  const switch_C_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_AshimawariNG3 form.C_AshimawariNG3:${form?.C_AshimawariNG3 || ""} params:`, params);
    return formProps.toggleSwitch("C_AshimawariNG3", { label: "車輌：足回りNGの場合 左後 1:有 9:無", ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右後 1:有 9:無スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右後 1:有 9:無スイッチのJSX要素
   */
  const switch_C_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_AshimawariNG4 form.C_AshimawariNG4:${form?.C_AshimawariNG4 || ""} params:`, params);
    return formProps.toggleSwitch("C_AshimawariNG4", { label: "車輌：足回りNGの場合 右後 1:有 9:無", ...params }, is_debug);
  };
  /**
   * 車輌：車輌看板スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌看板スイッチのJSX要素
   */
  const switch_C_SharyoKanban = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_C_SharyoKanban form.C_SharyoKanban:${form?.C_SharyoKanban || ""} params:`, params);
    return formProps.toggleSwitch("C_SharyoKanban", { label: "車輌：車輌看板", ...params }, is_debug);
  };
  /**
   * 価格の評価スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 価格の評価スイッチのJSX要素
   */
  const switch_Eval_Kakaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Eval_Kakaku form.Eval_Kakaku:${form?.Eval_Kakaku || ""} params:`, params);
    return formProps.toggleSwitch("Eval_Kakaku", { label: "価格の評価", ...params }, is_debug);
  };
  /**
   * 品質の評価スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 品質の評価スイッチのJSX要素
   */
  const switch_Eval_Hinshitsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Eval_Hinshitsu form.Eval_Hinshitsu:${form?.Eval_Hinshitsu || ""} params:`, params);
    return formProps.toggleSwitch("Eval_Hinshitsu", { label: "品質の評価", ...params }, is_debug);
  };
  /**
   * 梱包の評価スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 梱包の評価スイッチのJSX要素
   */
  const switch_Eval_Konpou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Eval_Konpou form.Eval_Konpou:${form?.Eval_Konpou || ""} params:`, params);
    return formProps.toggleSwitch("Eval_Konpou", { label: "梱包の評価", ...params }, is_debug);
  };
  /**
   * 納期の評価スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 納期の評価スイッチのJSX要素
   */
  const switch_Eval_Nouki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Eval_Nouki form.Eval_Nouki:${form?.Eval_Nouki || ""} params:`, params);
    return formProps.toggleSwitch("Eval_Nouki", { label: "納期の評価", ...params }, is_debug);
  };
  /**
   * 手配種別　1=自社 2=他社スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手配種別　1=自社 2=他社スイッチのJSX要素
   */
  const switch_PrepKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_PrepKind form.PrepKind:${form?.PrepKind || ""} params:`, params);
    return formProps.toggleSwitch("PrepKind", { label: "手配種別　1=自社 2=他社", ...params }, is_debug);
  };
  /**
   * QAフラグ 1=質問 2=回答スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} QAフラグ 1=質問 2=回答スイッチのJSX要素
   */
  const switch_QA_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_QA_flg form.QA_flg:${form?.QA_flg || ""} params:`, params);
    return formProps.toggleSwitch("QA_flg", { label: "QAフラグ 1=質問 2=回答", ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1:既読スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1:既読スイッチのJSX要素
   */
  const switch_al_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_al_flg form.al_flg:${form?.al_flg || ""} params:`, params);
    return formProps.toggleSwitch("al_flg", { label: "未読既読フラグ 1:既読", ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1=既読スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1=既読スイッチのJSX要素
   */
  const switch_al_flg2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_al_flg2 form.al_flg2:${form?.al_flg2 || ""} params:`, params);
    return formProps.toggleSwitch("al_flg2", { label: "未読既読フラグ 1=既読", ...params }, is_debug);
  };
  /**
   * 一覧表示フラグ NULL:表示　9:非表示スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 一覧表示フラグ NULL:表示　9:非表示スイッチのJSX要素
   */
  const switch_view_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_view_flg form.view_flg:${form?.view_flg || ""} params:`, params);
    return formProps.toggleSwitch("view_flg", { label: "一覧表示フラグ NULL:表示　9:非表示", ...params }, is_debug);
  };
  /**
   * 再見積フラグ　1:再見積済スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積フラグ　1:再見積済スイッチのJSX要素
   */
  const switch_Sai_MitsumoriFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sai_MitsumoriFlg form.Sai_MitsumoriFlg:${form?.Sai_MitsumoriFlg || ""} params:`, params);
    return formProps.toggleSwitch("Sai_MitsumoriFlg", { label: "再見積フラグ　1:再見積済", ...params }, is_debug);
  };
  /**
   * CO2集計 1=対象スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計 1=対象スイッチのJSX要素
   */
  const switch_co2_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_co2_flg form.co2_flg:${form?.co2_flg || ""} params:`, params);
    return formProps.toggleSwitch("co2_flg", { label: "CO2集計 1=対象", ...params }, is_debug);
  };
  /**
   * CO2集計リビルト 1=対象スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計リビルト 1=対象スイッチのJSX要素
   */
  const switch_co2_flg_r = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_co2_flg_r form.co2_flg_r:${form?.co2_flg_r || ""} params:`, params);
    return formProps.toggleSwitch("co2_flg_r", { label: "CO2集計リビルト 1=対象", ...params }, is_debug);
  };
  /**
   * 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざんスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざんスイッチのJSX要素
   */
  const switch_Soukou_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Soukou_flg form.Soukou_flg:${form?.Soukou_flg || ""} params:`, params);
    return formProps.toggleSwitch("Soukou_flg", { label: "走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざん", ...params }, is_debug);
  };
  /**
   * 損傷箇所：前部スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：前部スイッチのJSX要素
   */
  const switch_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou01 form.Sonshou01:${form?.Sonshou01 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou01", { label: "損傷箇所：前部", ...params }, is_debug);
  };
  /**
   * 損傷箇所：後部スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：後部スイッチのJSX要素
   */
  const switch_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou02 form.Sonshou02:${form?.Sonshou02 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou02", { label: "損傷箇所：後部", ...params }, is_debug);
  };
  /**
   * 損傷箇所：右側スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：右側スイッチのJSX要素
   */
  const switch_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou03 form.Sonshou03:${form?.Sonshou03 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou03", { label: "損傷箇所：右側", ...params }, is_debug);
  };
  /**
   * 損傷箇所：左側スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：左側スイッチのJSX要素
   */
  const switch_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou04 form.Sonshou04:${form?.Sonshou04 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou04", { label: "損傷箇所：左側", ...params }, is_debug);
  };
  /**
   * 損傷箇所：ルーフスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：ルーフスイッチのJSX要素
   */
  const switch_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou05 form.Sonshou05:${form?.Sonshou05 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou05", { label: "損傷箇所：ルーフ", ...params }, is_debug);
  };
  /**
   * 損傷箇所：盗難スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：盗難スイッチのJSX要素
   */
  const switch_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou06 form.Sonshou06:${form?.Sonshou06 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou06", { label: "損傷箇所：盗難", ...params }, is_debug);
  };
  /**
   * 損傷箇所：水没スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：水没スイッチのJSX要素
   */
  const switch_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou07 form.Sonshou07:${form?.Sonshou07 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou07", { label: "損傷箇所：水没", ...params }, is_debug);
  };
  /**
   * 損傷箇所：炎上スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：炎上スイッチのJSX要素
   */
  const switch_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou08 form.Sonshou08:${form?.Sonshou08 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou08", { label: "損傷箇所：炎上", ...params }, is_debug);
  };
  /**
   * 損傷箇所：エアバッグ展開スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：エアバッグ展開スイッチのJSX要素
   */
  const switch_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou09 form.Sonshou09:${form?.Sonshou09 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou09", { label: "損傷箇所：エアバッグ展開", ...params }, is_debug);
  };
  /**
   * 損傷箇所：横転スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：横転スイッチのJSX要素
   */
  const switch_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou10 form.Sonshou10:${form?.Sonshou10 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou10", { label: "損傷箇所：横転", ...params }, is_debug);
  };
  /**
   * 損傷箇所：下回りスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：下回りスイッチのJSX要素
   */
  const switch_Sonshou11 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou11 form.Sonshou11:${form?.Sonshou11 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou11", { label: "損傷箇所：下回り", ...params }, is_debug);
  };
  /**
   * 損傷箇所：詳細不明スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：詳細不明スイッチのJSX要素
   */
  const switch_Sonshou12 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Sonshou12 form.Sonshou12:${form?.Sonshou12 || ""} params:`, params);
    return formProps.toggleSwitch("Sonshou12", { label: "損傷箇所：詳細不明", ...params }, is_debug);
  };
  /**
   * 足回り　1:不明　2:OK　9:NGスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回り　1:不明　2:OK　9:NGスイッチのJSX要素
   */
  const switch_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_Ashimawari form.Ashimawari:${form?.Ashimawari || ""} params:`, params);
    return formProps.toggleSwitch("Ashimawari", { label: "足回り　1:不明　2:OK　9:NG", ...params }, is_debug);
  };
  /**
   * 足回りNG：左前スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左前スイッチのJSX要素
   */
  const switch_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_AshimawariNG1 form.AshimawariNG1:${form?.AshimawariNG1 || ""} params:`, params);
    return formProps.toggleSwitch("AshimawariNG1", { label: "足回りNG：左前", ...params }, is_debug);
  };
  /**
   * 足回りNG：右前スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右前スイッチのJSX要素
   */
  const switch_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_AshimawariNG2 form.AshimawariNG2:${form?.AshimawariNG2 || ""} params:`, params);
    return formProps.toggleSwitch("AshimawariNG2", { label: "足回りNG：右前", ...params }, is_debug);
  };
  /**
   * 足回りNG：左後スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左後スイッチのJSX要素
   */
  const switch_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_AshimawariNG3 form.AshimawariNG3:${form?.AshimawariNG3 || ""} params:`, params);
    return formProps.toggleSwitch("AshimawariNG3", { label: "足回りNG：左後", ...params }, is_debug);
  };
  /**
   * 足回りNG：右後スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右後スイッチのJSX要素
   */
  const switch_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_AshimawariNG4 form.AshimawariNG4:${form?.AshimawariNG4 || ""} params:`, params);
    return formProps.toggleSwitch("AshimawariNG4", { label: "足回りNG：右後", ...params }, is_debug);
  };
  /**
   * 閲覧フラグ　1=見積　2=もぎとりスイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 閲覧フラグ　1=見積　2=もぎとりスイッチのJSX要素
   */
  const switch_ViewFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_ViewFlg form.ViewFlg:${form?.ViewFlg || ""} params:`, params);
    return formProps.toggleSwitch("ViewFlg", { label: "閲覧フラグ　1=見積　2=もぎとり", ...params }, is_debug);
  };
  /**
   * マッチング対象：外装フロント　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装フロント　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD100 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD100 form.CBunruiCD100:${form?.CBunruiCD100 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD100", { label: "マッチング対象：外装フロント　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：外装右側面　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装右側面　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD110 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD110 form.CBunruiCD110:${form?.CBunruiCD110 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD110", { label: "マッチング対象：外装右側面　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：外装左側面　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装左側面　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD120 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD120 form.CBunruiCD120:${form?.CBunruiCD120 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD120", { label: "マッチング対象：外装左側面　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：外装リア　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装リア　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD140 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD140 form.CBunruiCD140:${form?.CBunruiCD140 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD140", { label: "マッチング対象：外装リア　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：エンジン　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：エンジン　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD2 form.CBunruiCD2:${form?.CBunruiCD2 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD2", { label: "マッチング対象：エンジン　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：ミッション　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：ミッション　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD3 form.CBunruiCD3:${form?.CBunruiCD3 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD3", { label: "マッチング対象：ミッション　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * マッチング対象：走行　1=有効　0=無効スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：走行　1=有効　0=無効スイッチのJSX要素
   */
  const switch_CBunruiCD4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_CBunruiCD4 form.CBunruiCD4:${form?.CBunruiCD4 || ""} params:`, params);
    return formProps.toggleSwitch("CBunruiCD4", { label: "マッチング対象：走行　1=有効　0=無効", ...params }, is_debug);
  };
  /**
   * ログイン後の初期表示ページ　1=部品　2=車輌スイッチ
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ログイン後の初期表示ページ　1=部品　2=車輌スイッチのJSX要素
   */
  const switch_DefaultPage = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`switch_DefaultPage form.DefaultPage:${form?.DefaultPage || ""} params:`, params);
    return formProps.toggleSwitch("DefaultPage", { label: "ログイン後の初期表示ページ　1=部品　2=車輌", ...params }, is_debug);
  };

  return {
    switch_Wellset_flg, switch_dl_flg, switch_Shorui_flg, switch_ShoruiSoryo_Flg, switch_ShoruiSoufu_Flg, switch_ImgCheck, switch_OpenCheck, switch_ContactKind, switch_Buysell_flg, switch_Tesuryo_flg, switch_CarView_flg, switch_C_Shorui_flg, switch_Hacchu_flg, switch_C_Recycle_flg, switch_C_Shareki, switch_C_Shufuku, switch_C_Sonshou01, switch_C_Sonshou02, switch_C_Sonshou03, switch_C_Sonshou04, switch_C_Sonshou05, switch_C_Sonshou06, switch_C_Sonshou07, switch_C_Sonshou08, switch_C_Sonshou09, switch_C_Sonshou10, switch_C_Ashimawari, switch_C_AshimawariNG1, switch_C_AshimawariNG2, switch_C_AshimawariNG3, switch_C_AshimawariNG4, switch_C_SharyoKanban, switch_Eval_Kakaku, switch_Eval_Hinshitsu, switch_Eval_Konpou, switch_Eval_Nouki, switch_PrepKind, switch_QA_flg, switch_al_flg, switch_al_flg2, switch_view_flg, switch_Sai_MitsumoriFlg, switch_co2_flg, switch_co2_flg_r, switch_Soukou_flg, switch_Sonshou01, switch_Sonshou02, switch_Sonshou03, switch_Sonshou04, switch_Sonshou05, switch_Sonshou06, switch_Sonshou07, switch_Sonshou08, switch_Sonshou09, switch_Sonshou10, switch_Sonshou11, switch_Sonshou12, switch_Ashimawari, switch_AshimawariNG1, switch_AshimawariNG2, switch_AshimawariNG3, switch_AshimawariNG4, switch_ViewFlg, switch_CBunruiCD100, switch_CBunruiCD110, switch_CBunruiCD120, switch_CBunruiCD140, switch_CBunruiCD2, switch_CBunruiCD3, switch_CBunruiCD4, switch_DefaultPage
  };
};

export default useSwitchEx;